import { Link } from 'react-router-dom';
import { Post } from '@/hooks/usePosts';
import { useCategories } from '@/hooks/useCategories';

interface ArticleCardProps {
  post: Post;
  variant?: 'default' | 'compact';
}

export default function ArticleCard({ post, variant = 'default' }: ArticleCardProps) {
  const { getCategoryById } = useCategories();
  const primaryCat = post.categories?.[0] ? getCategoryById(post.categories[0]) : null;
  const extraCount = (post.categories?.length ?? 0) - 1;
  const isCompact = variant === 'compact';

  const primaryLabel = primaryCat?.label.toUpperCase() ?? (post.categories?.[0] ?? '').toUpperCase();

  return (
    <Link to={`/post/${post.slug}`} className="group cursor-pointer block">
      <article className="relative bg-background-50 rounded-xl overflow-hidden border border-background-200/80 transform transition-all duration-300 ease-out hover:-translate-y-2 hover:border-primary-200">
        {/* Image */}
        <div className={`w-full ${isCompact ? 'h-32 sm:h-44' : 'h-36 sm:h-48 md:h-52'} overflow-hidden relative`}>
          <img
            src={post.image}
            alt={post.title}
            className="w-full h-full object-cover object-top scale-100 group-hover:scale-110 transition-transform duration-500 ease-out"
          />
          <div className="absolute inset-0 bg-foreground-950/0 group-hover:bg-foreground-950/10 transition-colors duration-300" />

          {/* Category badge on hover */}
          <div className="absolute top-3 left-3 transform translate-y-1 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-all duration-300 ease-out flex items-center gap-1 flex-wrap">
            <span className="bg-background-50/95 text-primary-500 text-xs font-bold tracking-widest uppercase px-2.5 py-1 rounded-full">
              {primaryLabel}
            </span>
            {extraCount > 0 && (
              <span className="bg-background-50/90 text-foreground-600 text-xs font-bold px-2 py-1 rounded-full">
                +{extraCount}
              </span>
            )}
          </div>
        </div>

        {/* Content */}
        <div className={`${isCompact ? 'p-3 md:p-4' : 'p-4 md:p-5'}`}>
          {/* Primary category label */}
          <div className="flex items-center gap-2 mb-2 group-hover:opacity-0 transition-opacity duration-200">
            <span className="text-primary-500 text-xs font-bold tracking-widest uppercase">
              {primaryLabel}
            </span>
            {extraCount > 0 && (
              <span className="text-foreground-400 text-xs font-semibold bg-background-100 px-1.5 py-0.5 rounded-full">
                +{extraCount}
              </span>
            )}
          </div>

          <h3 className={`font-heading font-bold text-foreground-950 leading-snug mb-3 line-clamp-2 group-hover:text-primary-600 transition-colors duration-200 ${isCompact ? 'text-sm' : 'text-sm md:text-base'}`}>
            {post.title}
          </h3>

          {!isCompact && post.subtitle && (
            <p className="text-foreground-500 text-xs md:text-sm leading-relaxed line-clamp-2 mb-4">
              {post.subtitle}
            </p>
          )}

          <div className="flex items-center justify-between">
            <span className="text-foreground-400 text-xs flex items-center gap-1">
              <i className="ri-time-line text-xs"></i>
              {post.readTime}
            </span>
            <span className="text-primary-500 text-xs font-bold flex items-center gap-1 translate-x-2 opacity-0 group-hover:translate-x-0 group-hover:opacity-100 transition-all duration-300 ease-out whitespace-nowrap">
              Ler artigo
              <i className="ri-arrow-right-line text-xs group-hover:translate-x-0.5 transition-transform duration-200"></i>
            </span>
          </div>
        </div>
      </article>
    </Link>
  );
}